import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import { Search as SearchIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Search() {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("active");
  const [results, setResults] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      api.get("/search", { params: { q, filter } }).then((r) => setResults(r.data));
    }, 150);
    return () => clearTimeout(timer);
  }, [q, filter]);

  return (
    <div className="p-6 md:p-10">
      <p className="eyebrow">Roster Lookup</p>
      <h1 className="heading-1 mb-6">Player <span className="text-burgundy">Search</span></h1>

      <div className="bg-ink-800 rounded-lg border border-white/5 p-4 mb-6">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, jersey number, or position…"
            className="w-full bg-ink-900 border border-ink-700 rounded-md pl-10 pr-4 py-3 text-white placeholder:text-ink-400 focus:outline-none focus:border-burgundy"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2 mt-3">
          {[
            { v: "active", label: "Active" },
            { v: "all", label: "All Players" },
            { v: "alumni", label: "Alumni" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              className={cn(
                "px-4 py-1.5 rounded-md text-xs uppercase tracking-wider font-semibold transition-colors",
                filter === f.v ? "bg-burgundy text-white" : "bg-ink-900 text-ink-300 hover:bg-ink-700"
              )}
              data-testid={`filter-${f.v}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {results.length === 0 && <p className="text-ink-400 text-sm">No players match.</p>}
        {results.map((p) => (
          <Link
            key={p.id}
            to={`/player/${p.slug}`}
            className="flex items-center gap-4 p-4 bg-ink-800 rounded-lg border border-white/5 hover:border-burgundy/40 hover:bg-ink-900 transition"
            data-testid={`search-result-${p.slug}`}
          >
            <PlayerAvatar player={p} size="md" />
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg font-bold uppercase truncate">{p.player_name}</p>
              <p className="text-ink-400 text-xs">#{p.jersey_number || "—"} · {p.position || "—"} · {p.grade ? `Grade ${p.grade}` : "—"}</p>
            </div>
            <div className="text-right">
              <p className="eyebrow text-[9px]">PPG</p>
              <p className="font-display text-2xl font-bold">{p.ppg}</p>
            </div>
            <span className={cn(
              "ml-3 px-2 py-1 text-[10px] uppercase tracking-wider rounded",
              p.status === "active" ? "bg-green-500/15 text-green-400" : "bg-ink-700 text-ink-300"
            )}>
              {p.status === "active" ? "Active" : `Alumni${p.graduation_year ? ` '${String(p.graduation_year).slice(-2)}` : ""}`}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
